import React, { useEffect, useLayoutEffect, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import AddButton from "components/buttons/addbutton";
import ListHeadingComponent from "components/headingComponent/listHeading";
import {
  and,
  collection,
  deleteDoc,
  doc,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  or,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { auth, dbFirestore } from "helper/config/firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import RoutingPaths from "helper/routingPath";
import TableName from "helper/tableName/tableName";
import Constant from "helper/constant/constant";
import ToastMessages from "helper/toastMessages";
import Moment from "react-moment";
import Dateformat from "helper/constant/timeFormat";
import { Button } from "primereact/button";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { ButtonGroup } from "react-bootstrap";
import CommonHeadings from "helper/constant/headings";

const UsersList = () => {
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  //these are pagination variables
  const [page, setPage] = useState(0);
  const [isNext, setIsNext] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // MARK: use Effect function
  useEffect(() => {
    checkUserRole();
    fetchUsersApi();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

   // use layout effect function
   useLayoutEffect(() => {
    document.title = CommonHeadings.userListHeading;
  }, []);
  
  // MARK: checkUserRole
  function checkUserRole() {
    const userInfo = JSON.parse(localStorage.getItem("session"));
    setIsAdmin(userInfo.role === "admin");
  }

  // MARK: fetch users API
  async function fetchUsersApi() {
    let q = query(
      collection(dbFirestore, TableName.users),
      orderBy("created_at", "desc"),
      limit(Constant.perPage)
    );
    const querySnapshot = await getDocs(q);

    setUsersData(
      querySnapshot.docs.map((docs) => ({ ...docs.data(), id: docs.id }))
    );
    setLoading(false);
    setPage(0);
    setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
  }

  // MARK: Delete data function
  const tapOnDeleteConfirm = (user) => () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete this batch.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--delete-button-color)",
      cancelButtonColor: "var(--cancel-button-list)",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        deleteUsersApi(user);
      }
    });
  };

  // MARK: delete API
  const deleteUsersApi = async (user) => {
    await deleteDoc(doc(dbFirestore, TableName.users, user.id));
    setUsersData((prevState) => prevState.filter((el) => el.id !== user.id));
    toast.success(ToastMessages.batchDeleted);
  };

  // MARK: change password (send reset email)
  const tapOnChangePassword = (user) => () => {
    Swal.fire({
      title: "Send Reset Password Email",
      text: `Send a password reset email to ${user.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "var(--primary-color)",
      cancelButtonColor: "var(--cancel-button-list)",
      confirmButtonText: "Send Email",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await sendPasswordResetEmail(auth, user.email);
          toast.success(`Password reset email sent to ${user.email}. Ask the user to also check their spam folder.`);
        } catch (error) {
          console.error("Reset email error:", error.code, error.message);
          if (error.code === "auth/user-not-found") {
            toast.error(`No Firebase Auth account found for ${user.email}. The user may need to be re-created.`);
          } else {
            toast.error(`Failed to send reset email: ${error.message}`);
          }
        }
      }
    });
  };

  // MARK: serach api
  const searchUsersApi = async (e) => {
    const searchText = e.target.value.toLowerCase();
    setGlobalFilterValue(searchText);

    if (searchText) {
      let q = query(
        collection(dbFirestore, TableName.users),
        or(
          and(
            where("name", ">=", searchText),
            where("name", "<=", searchText + "\uf8ff")
          ),
          and(
            where(
              "name",
              ">=",
              searchText.charAt(0).toUpperCase() + searchText.slice(1)
            ),
            where(
              "name",
              "<=",
              searchText.charAt(0).toUpperCase() +
                searchText.slice(1) +
                "\uf8ff"
            )
          ),
          and(
            where(
              "name",
              ">=",
              searchText.charAt(0).toLowerCase() + searchText.slice(1)
            ),
            where(
              "name",
              "<=",
              searchText.charAt(0).toLowerCase() +
                searchText.slice(1) +
                "\uf8ff"
            )
          )
        )
      );
      const querySnapshot = await getDocs(q);

      setUsersData(
        querySnapshot.docs.map((docs) => ({ ...docs.data(), id: docs.id }))
      );
    } else {
      fetchUsersApi();
    }
  };

  // MARK: pagination code
  const showNext = ({ item }) => {
    setPage(page + 1);

    const fetchNextData = async () => {
      let q = query(
        collection(dbFirestore, TableName.users),
        orderBy("created_at", "desc"),
        limit(Constant.perPage),
        startAfter(item.created_at)
      );

      const querySnapshot = await getDocs(q);

      setUsersData(
        querySnapshot.docs.map((docs) => ({ ...docs.data(), id: docs.id }))
      );
      setPage(page + 1);
      setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
    };

    if (usersData.length === 0) {
      alert(ToastMessages.alertMessage);
    } else {
      fetchNextData();
    }
  };

  // MARK: previous data function using pagination previous button
  const fetchPreviousData = async () => {
    setPage(page - 1);

    let q = query(
      collection(dbFirestore, TableName.users),
      orderBy("created_at", "desc"),
      endBefore(usersData[0].created_at),
      limitToLast(Constant.perPage)
    );

    const querySnapshot = await getDocs(q);

    setUsersData(
      querySnapshot.docs.map((docs) => ({ ...docs.data(), id: docs.id }))
    );
    setPage(page - 1);
    setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
  };

  // MARK: condition while selecting the previous button
  const showPrevious = () => {
    if (usersData.length === 0) {
      fetchUsersApi();
    } else {
      fetchPreviousData();
    }
  };

  //MARK: Search UI
  const searchBar = () => {
    return (
      <>
        <div className="search__main_div">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              type="search"
              value={globalFilterValue}
              onChange={searchUsersApi}
              placeholder="Search by name"
            />
          </IconField>
        </div>
      </>
    );
  };

  const header = searchBar();

  // MARK:users columns data
  const usersColumns = [
    {
      field: "name",
      header: "Name",
      className: "table__column__capital",
    },
    {
      field: "email",
      header: "Email",
    },
    {
      field: "role",
      header: "Role",
      className: "table__column__capital",
    },
    {
      field: "created_at",
      header: "Created At",
      CardBody: (data) => (
        <Moment format={Dateformat.DDMMYYYHHMMSS}>{data.created_at}</Moment>
      ),
    },
  ];

  if (isLoading) {
    return <div className="loading-div"> Loading ... </div>;
  }

  return (
    <div className="modules__main__div">
      <div className="col-12">
        <div className="card modules__card">
          <div className="modules__lists_main-content">
            <ListHeadingComponent classname="flex-grow-1" headingName={CommonHeadings.userListHeading} />
            <AddButton navigation={RoutingPaths.addUsers} />
          </div>

          <DataTable
            className="mt-5 mb-3"
            value={usersData}
            header={header}
            emptyMessage="No data found."
          >
            {usersColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                bodyClassName={col.className}
                body={col.CardBody}
              />
            ))}

            {isAdmin && (
              <Column
                field="actions"
                header="Actions"
                body={(data) => (
                  <>
                    <div className="d-flex mt-3 text-start gap-2">
                      <Button
                        severity="info"
                        type="button"
                        onClick={tapOnChangePassword(data)}
                        className="lists__edit_button"
                        tooltip="Send Reset Password Email"
                        tooltipOptions={{ position: "bottom" }}
                      >
                        <span className="pi pi-key"></span>
                      </Button>
                      <Button
                        severity="secondary"
                        type="button"
                        onClick={tapOnDeleteConfirm(data)}
                        className="lists__delete_button"
                        tooltip="delete"
                        tooltipOptions={{ position: "bottom" }}
                      >
                        <span className="pi pi-trash"></span>{" "}
                      </Button>
                      <ToastContainer />
                    </div>
                  </>
                )}
              ></Column>
            )}
          </DataTable>

          <ButtonGroup
            style={{ gap: "1rem", alignSelf: "center" }}
            className="mt-5"
          >
            {
              <button
                disabled={page === 0 ? true : false}
                onClick={() => showPrevious()}
                className={
                  page === 0 ? "previous-disabled-button" : "previous-button"
                }
              >
                <span className="pi pi-angle-left">Previous</span>
              </button>
            }

            {
              <button
                disabled={isNext}
                onClick={() =>
                  showNext({
                    item: usersData[usersData.length - 1],
                  })
                }
                className={
                  isNext ? "previous-disabled-button" : "previous-button"
                }
              >
                Next<span className="pi pi-angle-right"></span>
              </button>
            }
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};

export default UsersList;
